import * as THREE from 'three'

import { Wad } from '@/cs/wad'

const WAD_URL = '/cs/wads/decals.wad'
const SHOT_NAMES = ['{shot1', '{shot2', '{shot3', '{shot4', '{shot5']
const POOL_SIZE = 64
// The mark only fills the centre ~1/3 of the 16px texture, so the quad is sized
// up to ~18u to leave a visible ~6u bullet hole on the wall.
const DECAL_SIZE = 18

/**
 * Persistent bullet-hole decals. Unlike the brief impact spark, these stay on
 * the wall: a ring buffer of textured quads using the real CS `{shot` decals
 * from decals.wad, oriented to the hit surface. The oldest is recycled once the
 * pool is full, so the scene never accumulates unbounded geometry.
 */
export default class Decals {
  private readonly pool: THREE.Mesh[] = []
  private textures: THREE.Texture[] = []
  private next = 0
  private ready = false
  private readonly tmp = new THREE.Vector3()

  constructor(private readonly scene: THREE.Scene) {
    void this.load()
  }

  private async load(): Promise<void> {
    try {
      const res = await fetch(WAD_URL)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const wad = new Wad(await res.arrayBuffer())

      this.textures = SHOT_NAMES.map((name) => {
        const dec = wad.getDecal(name)
        if (!dec) throw new Error(`decal ${name} missing`)
        const tex = new THREE.DataTexture(
          dec.rgba,
          dec.width,
          dec.height,
          THREE.RGBAFormat,
        )
        tex.colorSpace = THREE.SRGBColorSpace
        tex.magFilter = THREE.LinearFilter
        tex.minFilter = THREE.LinearFilter
        tex.needsUpdate = true
        return tex
      })

      const geometry = new THREE.PlaneGeometry(DECAL_SIZE, DECAL_SIZE)
      for (let i = 0; i < POOL_SIZE; i++) {
        const material = new THREE.MeshBasicMaterial({
          map: this.textures[0],
          transparent: true,
          depthWrite: false,
          // Lift the decal slightly toward the camera so it doesn't z-fight the wall.
          polygonOffset: true,
          polygonOffsetFactor: -4,
          polygonOffsetUnits: -4,
          side: THREE.DoubleSide,
        })
        const mesh = new THREE.Mesh(geometry, material)
        mesh.visible = false
        mesh.frustumCulled = false
        mesh.renderOrder = 2
        this.scene.add(mesh)
        this.pool.push(mesh)
      }
      this.ready = true
      console.info('[cs] bullet decals ready')
    } catch (error) {
      console.warn('[cs] decals failed to load:', error)
    }
  }

  /** Stamp a lasting bullet hole at `point`, facing along the surface `normal`. */
  public spawn(point: THREE.Vector3, normal: THREE.Vector3): void {
    if (!this.ready) return
    const mesh = this.pool[this.next]
    this.next = (this.next + 1) % this.pool.length

    const material = mesh.material as THREE.MeshBasicMaterial
    material.map = this.textures[(Math.random() * this.textures.length) | 0]
    material.needsUpdate = true

    mesh.position.copy(point).addScaledVector(normal, 0.2) // off the surface
    mesh.lookAt(this.tmp.copy(mesh.position).add(normal))
    mesh.rotation.z = Math.random() * Math.PI * 2
    mesh.scale.setScalar(0.8 + Math.random() * 0.5)
    mesh.visible = true
  }
}
